import React, { useState } from 'react';
import { Search, Sparkles, ChevronDown, ChevronUp, CheckCircle, AlertCircle, XCircle, TrendingUp, Send, Building2, MapPin } from 'lucide-react';
// Mock data per le aziende
const mockCompanies = [{
  id: '1',
  name: 'Luxury Fashion Brand',
  category: 'Fashion'
}, {
  id: '2',
  name: 'Jewelry Brand',
  category: 'Luxury'
}, {
  id: '3',
  name: 'Tech Startup',
  category: 'Technology'
}];
// Mock data per le esigenze esistenti
const mockRequirements = [{
  id: 1,
  companyId: '1',
  company: 'Luxury Fashion Brand',
  description: 'Cerchiamo uno spazio retail di circa 150m² in zona Quadrilatero della Moda, preferibilmente su strada ad alto traffico. Budget fino a €1.000.000. Necessario piano terra con vetrine ampie.'
}, {
  id: 2,
  companyId: '1',
  company: 'Luxury Fashion Brand',
  description: 'Showroom per presentazione collezioni 120-150m² in zona Brera o Porta Nuova. Budget €800.000-€1.000.000. Preferibile spazio con mezzanino e possibilità eventi.'
}, {
  id: 3,
  companyId: '2',
  company: 'Jewelry Brand',
  description: 'Boutique esclusiva 80-100m² in Via Montenapoleone o Via della Spiga. Budget fino a €900.000. Richiesto piano terra con possibilità di personalizzazione.'
}, {
  id: 4,
  companyId: '2',
  company: 'Jewelry Brand',
  description: 'Secondo punto vendita in zona Centro Storico Roma, 70-90m² con vetrine ampie. Budget €700.000-€850.000. Preferenza per Via Condotti o vie limitrofe.'
}, {
  id: 5,
  companyId: '3',
  company: 'Tech Startup',
  description: 'Showroom moderno per presentazione prodotti, 120-180m² in zona Centro o Porta Garibaldi. Budget €800.000-€1.200.000. Preferibile spazio con mezzanino.'
}];
// Mock data per gli immobili matchati
const mockMatchedProperties = [{
  id: 1,
  address: 'Via della Spiga 8, Milano',
  zone: 'Quadrilatero',
  price: 650000,
  size: 90,
  type: 'Boutique',
  floor: 'Piano Terra',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/gggVNqt7oMzSPTA1GSg7hs/a0230d7093e3a80e46b36f3aff623d30.webp',
  matchScore: 95,
  satisfied: ['Posizione in Via della Spiga come richiesto', 'Piano terra con vetrine ampie', 'Zona ad altissimo traffico pedonale', 'Possibilità di personalizzazione completa'],
  acceptable: ['Superficie 90m² leggermente inferiore ai 100m² ideali', 'Prezzo €650.000 sotto il budget massimo'],
  notSatisfied: []
}, {
  id: 2,
  address: 'Via Montenapoleone 12, Milano',
  zone: 'Centro',
  price: 850000,
  size: 120,
  type: 'Retail',
  floor: 'Piano Terra',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/4YkAY7UqKQUPpQ92yzEMgw/681b7f504e32f.jpg',
  matchScore: 87,
  satisfied: ['Posizione premium in Via Montenapoleone', 'Piano terra con ampie vetrine', 'Superficie 120m² adeguata'],
  acceptable: ['Prezzo €850.000 vicino al budget massimo', 'Tipologia Retail invece di Boutique specifica'],
  notSatisfied: []
}, {
  id: 3,
  address: 'Via Borgospesso 15, Milano',
  zone: 'Quadrilatero',
  price: 760000,
  size: 105,
  type: 'Boutique',
  floor: 'Piano Terra',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/nApHqCsLnhTw7aP1mFPzUT/Rodeo-Drive-Negozi.jpg',
  matchScore: 82,
  satisfied: ['Zona Quadrilatero come richiesto', 'Superficie 105m² nel range ideale', 'Piano terra'],
  acceptable: ['Via Borgospesso, non Via Montenapoleone o Via della Spiga', 'Prezzo €760.000 sotto budget'],
  notSatisfied: []
}];
type ViewState = 'initial' | 'loading' | 'results';
export function NeedsMatching() {
  const [viewState, setViewState] = useState<ViewState>('initial');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedRequirement, setSelectedRequirement] = useState('');
  const [customRequirement, setCustomRequirement] = useState('');
  const [expandedProperty, setExpandedProperty] = useState<number | null>(null);
  const [analyzedRequirement, setAnalyzedRequirement] = useState('');
  // Filtra le esigenze in base all'azienda selezionata
  const filteredRequirements = selectedCompany ? mockRequirements.filter(req => req.companyId === selectedCompany) : [];
  const handleSearch = () => {
    const requirement = selectedRequirement ? mockRequirements.find(r => r.id.toString() === selectedRequirement)?.description : customRequirement;
    if (!requirement) return;
    setAnalyzedRequirement(requirement);
    setViewState('loading');
    setTimeout(() => {
      setViewState('results');
    }, 3000);
  };
  const handleReset = () => {
    setViewState('initial');
    setSelectedCompany('');
    setSelectedRequirement('');
    setCustomRequirement('');
    setExpandedProperty(null);
    setAnalyzedRequirement('');
  };
  const toggleProperty = (id: number) => {
    setExpandedProperty(expandedProperty === id ? null : id);
  };
  if (viewState === 'initial') {
    return <div className="space-y-6" data-id="element-3269">
        <div className="flex items-center justify-between" data-id="element-3270">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-3271">
            Needs Matching
          </h1>
        </div>
        <div className="bg-white p-6 border border-gray-200 rounded-lg space-y-6" data-id="element-3272">
          <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-3273">
            <Sparkles className="w-5 h-5 text-[#1388ad] flex-shrink-0 mt-0.5" data-id="element-3274" />
            <div data-id="element-3275">
              <h3 className="text-sm font-normal text-gray-900 mb-1" data-id="element-3276">
                Matching Intelligente con AI
              </h3>
              <p className="text-xs text-gray-600" data-id="element-3277">
                L'intelligenza artificiale analizzerà le esigenze e troverà gli
                immobili più compatibili, fornendo una spiegazione dettagliata
                per ogni match.
              </p>
            </div>
          </div>
          <div className="space-y-4" data-id="element-3278">
            <div data-id="element-3279">
              <label className="block text-sm font-normal text-gray-700 mb-2" data-id="element-3280">
                Seleziona un'azienda
              </label>
              <select value={selectedCompany} onChange={e => {
              setSelectedCompany(e.target.value);
              setSelectedRequirement('');
              setCustomRequirement('');
            }} className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1388ad]" data-id="element-3281">
                <option value="" data-id="element-3282">-- Seleziona un'azienda --</option>
                {mockCompanies.map(company => <option key={company.id} value={company.id} data-id="element-3283">
                    {company.name} - {company.category}
                  </option>)}
              </select>
            </div>
            {selectedCompany && <div data-id="element-3284">
                <label className="block text-sm font-normal text-gray-700 mb-2" data-id="element-3285">
                  Seleziona un'esigenza
                </label>
                <select value={selectedRequirement} onChange={e => {
              setSelectedRequirement(e.target.value);
              setCustomRequirement('');
            }} className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1388ad]" data-id="element-3286">
                  <option value="" data-id="element-3287">-- Seleziona un'esigenza --</option>
                  {filteredRequirements.map(req => <option key={req.id} value={req.id} data-id="element-3288">
                      Esigenza #{req.id} - {req.description.substring(0, 50)}...
                    </option>)}
                </select>
                {selectedRequirement && <div className="mt-3 p-4 bg-gray-50 border border-gray-200 rounded-lg" data-id="element-3289">
                    <p className="text-xs text-gray-600" data-id="element-3290">
                      {mockRequirements.find(r => r.id.toString() === selectedRequirement)?.description}
                    </p>
                  </div>}
              </div>}
            <div className="flex items-center gap-3" data-id="element-3291">
              <div className="flex-1 h-px bg-gray-200" data-id="element-3292" />
              <span className="text-xs text-gray-500" data-id="element-3293">oppure</span>
              <div className="flex-1 h-px bg-gray-200" data-id="element-3294" />
            </div>
            <div data-id="element-3295">
              <label className="block text-sm font-normal text-gray-700 mb-2" data-id="element-3296">
                Inserisci una nuova esigenza
              </label>
              <textarea value={customRequirement} onChange={e => {
              setCustomRequirement(e.target.value);
              setSelectedCompany('');
              setSelectedRequirement('');
            }} placeholder="Descrivi l'esigenza del cliente: zona preferita, metratura, budget, caratteristiche specifiche..." rows={4} className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1388ad] resize-none" data-id="element-3297" />
            </div>
          </div>
          <button onClick={handleSearch} disabled={!selectedRequirement && !customRequirement.trim()} className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#1388ad] text-white text-sm font-normal hover:bg-[#0f6b8a] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors rounded-full" data-id="element-3298">
            <Search className="w-4 h-4" data-id="element-3299" />
            Trova Immobili Compatibili
          </button>
        </div>
      </div>;
  }
  if (viewState === 'loading') {
    return <div className="space-y-6" data-id="element-3300">
        <div className="flex items-center justify-between" data-id="element-3301">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-3302">
            Needs Matching
          </h1>
        </div>
        <div className="bg-white p-8 border border-gray-200 rounded-lg" data-id="element-3303">
          <div className="flex flex-col items-center justify-center py-12" data-id="element-3304">
            <div className="relative w-16 h-16 mb-6" data-id="element-3305">
              <div className="absolute inset-0 border-4 border-gray-200 rounded-full" data-id="element-3306" />
              <div className="absolute inset-0 border-4 border-[#1388ad] rounded-full border-t-transparent animate-spin" data-id="element-3307" />
              <Sparkles className="absolute inset-0 m-auto w-6 h-6 text-[#1388ad]" data-id="element-3308" />
            </div>
            <h3 className="text-lg font-normal text-gray-900 mb-2" data-id="element-3309">
              Analisi in corso...
            </h3>
            <p className="text-sm text-gray-600 text-center max-w-md" data-id="element-3310">
              L'AI sta analizzando le esigenze e cercando gli immobili più
              adatti nel database
            </p>
          </div>
          <div className="space-y-4 mt-8" data-id="element-3311">
            {[1, 2, 3].map(i => <div key={i} className="border border-gray-200 rounded-lg p-4 animate-pulse" data-id="element-3312">
                <div className="flex gap-4" data-id="element-3313">
                  <div className="w-24 h-24 bg-gray-200 rounded-lg flex-shrink-0" data-id="element-3314" />
                  <div className="flex-1 space-y-3" data-id="element-3315">
                    <div className="h-4 bg-gray-200 rounded w-3/4" data-id="element-3316" />
                    <div className="h-3 bg-gray-200 rounded w-1/2" data-id="element-3317" />
                    <div className="h-3 bg-gray-200 rounded w-2/3" data-id="element-3318" />
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </div>;
  }
  return <div className="space-y-6" data-id="element-3319">
      <div className="flex items-center justify-between" data-id="element-3320">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-3321">Needs Matching</h1>
        <div className="flex items-center gap-3" data-id="element-3322">
          <button onClick={handleReset} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors rounded-full border border-gray-300" data-id="element-3323">
            Nuova Ricerca
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-[#1388ad] text-white rounded-full hover:bg-[#0f6b8a] transition-colors" data-id="element-3324">
            <Send className="w-4 h-4" data-id="element-3325" />
            Invia Campagna
          </button>
        </div>
      </div>
      <div className="bg-gradient-to-r from-[#1388ad] to-[#0f6b8a] p-6 border border-gray-200 rounded-lg text-white" data-id="element-3326">
        <div className="flex items-start gap-3 mb-3" data-id="element-3327">
          <Sparkles className="w-5 h-5 flex-shrink-0 mt-0.5" data-id="element-3328" />
          <h3 className="text-sm font-normal" data-id="element-3329">Esigenza Analizzata</h3>
        </div>
        <p className="text-sm opacity-90 leading-relaxed" data-id="element-3330">
          {analyzedRequirement}
        </p>
      </div>
      <div className="bg-white p-4 border border-gray-200 rounded-lg" data-id="element-3331">
        <div className="flex items-center justify-between" data-id="element-3332">
          <div className="flex items-center gap-2" data-id="element-3333">
            <Building2 className="w-5 h-5 text-[#1388ad]" data-id="element-3334" />
            <h2 className="text-lg font-normal text-gray-900" data-id="element-3335">
              {mockMatchedProperties.length} Immobili Compatibili
            </h2>
          </div>
          <span className="text-xs text-gray-500" data-id="element-3336">
            Ordinati per compatibilità
          </span>
        </div>
      </div>
      <div className="space-y-4" data-id="element-3337">
        {mockMatchedProperties.map(property => <div key={property.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow" data-id="element-3338">
            <div className="p-4" data-id="element-3339">
              <div className="flex gap-4" data-id="element-3340">
                <img src={property.image} alt={property.address} className="w-24 h-24 rounded-lg object-cover flex-shrink-0" data-id="element-3341" />
                <div className="flex-1 min-w-0" data-id="element-3342">
                  <div className="flex items-start justify-between gap-3 mb-2" data-id="element-3343">
                    <div className="flex-1 min-w-0" data-id="element-3344">
                      <h3 className="text-sm font-normal text-gray-900 mb-1" data-id="element-3345">
                        {property.address}
                      </h3>
                      <div className="flex items-center gap-2 text-xs text-gray-500" data-id="element-3346">
                        <MapPin className="w-3 h-3" data-id="element-3347" />
                        <span data-id="element-3348">{property.zone}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0" data-id="element-3349">
                      <div className="text-right" data-id="element-3350">
                        <div className="text-2xl font-semibold text-[#1388ad]" data-id="element-3351">
                          {property.matchScore}%
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-3352">
                          compatibilità
                        </div>
                      </div>
                      <TrendingUp className="w-5 h-5 text-[#1388ad]" data-id="element-3353" />
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-600" data-id="element-3354">
                    <span className="px-2 py-1 bg-gray-100 rounded-full" data-id="element-3355">
                      {property.type}
                    </span>
                    <span data-id="element-3356">{property.size} m²</span>
                    <span data-id="element-3357">{property.floor}</span>
                    <span className="text-[#1388ad] font-normal" data-id="element-3358">
                      €{property.price.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="border-t border-gray-200" data-id="element-3359">
              <button onClick={() => toggleProperty(property.id)} className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors" data-id="element-3360">
                <span className="text-sm text-gray-700" data-id="element-3361">Dettagli Matching</span>
                {expandedProperty === property.id ? <ChevronUp className="w-4 h-4 text-gray-500" data-id="element-3362" /> : <ChevronDown className="w-4 h-4 text-gray-500" data-id="element-3363" />}
              </button>
              {expandedProperty === property.id && <div className="px-4 pb-4 space-y-4" data-id="element-3364">
                  {property.satisfied.length > 0 && <div data-id="element-3365">
                      <div className="flex items-center gap-2 mb-2" data-id="element-3366">
                        <CheckCircle className="w-4 h-4 text-green-600" data-id="element-3367" />
                        <h4 className="text-sm font-normal text-gray-900" data-id="element-3368">
                          Requisiti Soddisfatti
                        </h4>
                      </div>
                      <ul className="space-y-1.5 ml-6" data-id="element-3369">
                        {property.satisfied.map((item, index) => <li key={index} className="text-xs text-gray-600 flex items-start gap-2" data-id="element-3370">
                            <span className="text-green-600 mt-0.5" data-id="element-3371">•</span>
                            <span data-id="element-3372">{item}</span>
                          </li>)}
                      </ul>
                    </div>}
                  {property.acceptable.length > 0 && <div data-id="element-3373">
                      <div className="flex items-center gap-2 mb-2" data-id="element-3374">
                        <AlertCircle className="w-4 h-4 text-yellow-600" data-id="element-3375" />
                        <h4 className="text-sm font-normal text-gray-900" data-id="element-3376">
                          Compromessi Accettabili
                        </h4>
                      </div>
                      <ul className="space-y-1.5 ml-6" data-id="element-3377">
                        {property.acceptable.map((item, index) => <li key={index} className="text-xs text-gray-600 flex items-start gap-2" data-id="element-3378">
                            <span className="text-yellow-600 mt-0.5" data-id="element-3379">•</span>
                            <span data-id="element-3380">{item}</span>
                          </li>)}
                      </ul>
                    </div>}
                  {property.notSatisfied.length > 0 && <div data-id="element-3381">
                      <div className="flex items-center gap-2 mb-2" data-id="element-3382">
                        <XCircle className="w-4 h-4 text-red-600" data-id="element-3383" />
                        <h4 className="text-sm font-normal text-gray-900" data-id="element-3384">
                          Requisiti Non Soddisfatti
                        </h4>
                      </div>
                      <ul className="space-y-1.5 ml-6" data-id="element-3385">
                        {property.notSatisfied.map((item, index) => <li key={index} className="text-xs text-gray-600 flex items-start gap-2" data-id="element-3386">
                            <span className="text-red-600 mt-0.5" data-id="element-3387">•</span>
                            <span data-id="element-3388">{item}</span>
                          </li>)}
                      </ul>
                    </div>}
                </div>}
            </div>
          </div>)}
      </div>
    </div>;
}